import React from 'react'
// libs
import { useDispatch, useSelector } from 'react-redux'
import {
  change,
  reduxForm,
  arrayPush,
  initialize,
  arrayRemove,
  clearFields,
  arrayRemoveAll,
  formValueSelector,
  InjectedFormProps
} from 'redux-form'
// components
import { Header } from 'components/Modals/PlanItems/styles'
import { Button, BUTTON_LAYOUTS, DrawerModal } from 'components/UI'
// view components
import Field from './components/Field'
import Fields from './components/Fields'
import Filters from './components/Filters'
// themes
import { CloseIcon } from 'components/UI/Icons'
// constants
import { Forms, Modals } from 'constants/index'
// hooks
import { useModalData } from 'hooks/index'
import { usePlanFilterFields } from 'hooks/plans/usePlanFilterFields'
// context
import { PlanEditPageContext } from 'containers/Plan/Edit/layers/API'
// icons
import PlusIcon from '@mui/icons-material/Add'
import IconButton from '@mui/material/IconButton'
// themes
import { colors } from 'themes/index'
// types
import { IFieldFilter } from 'types/graphql'
import { PlanFilterField } from 'hooks/plans/usePlanFilterFields'
// views
import { ButtonsWrapper, Container, Wrapper } from './views'
// utils
import { generateFieldFilter, generateFieldValueFromFilter } from './utils'

const PAPER_PROPS = { style: { overflow: 'hidden' } }

export type FilterField = IFieldFilter & { name: string; entityName?: string; systemName?: string }

const selector = formValueSelector(Forms.PLAN_FILTER_FORM)

const PlanFilterDrawer: React.FC<InjectedFormProps<any>> = () => {
  const [currentField, setCurrentField] = React.useState<(PlanFilterField & { idx?: number }) | null>(null)
  const [isConfigured, setIsConfigured] = React.useState(false)

  const { fieldFilters: appliedFieldFilters, setFieldFilters } = React.useContext(PlanEditPageContext)

  const dispatch = useDispatch()

  const filter = useSelector((state) => selector(state, 'filter') || [])
  const search = useSelector((state) => selector(state, 'search') || '')

  const { hideModal, modalData: { isOpen } } = useModalData({ modalType: Modals.PLAN_FILTER_MODAL })

  const { fields } = usePlanFilterFields(search)

  const addFilter = (field: FilterField) => {
    setCurrentField(null)
    setIsConfigured(false)

    if (currentField.idx || currentField.idx === 0) {
      dispatch(change(Forms.PLAN_FILTER_FORM, `filter[${currentField.idx}]`, field))
    } else {
      dispatch(arrayPush(Forms.PLAN_FILTER_FORM, 'filter', field))
    }

    dispatch(clearFields(Forms.PLAN_FILTER_FORM, false, false, currentField.id))
  }

  const removeFilter = (idx: number) => {
    dispatch(arrayRemove(Forms.PLAN_FILTER_FORM, 'filter', idx))
  }

  const clearFilters = () => {
    setCurrentField(null)
    setIsConfigured(false)
    dispatch(arrayRemoveAll(Forms.PLAN_FILTER_FORM, 'filter'))
  }

  const closeDrawer = () => {
    hideModal()
    clearFilters()
  }

  const clearField = () => {
    if (currentField.idx || currentField.idx === 0) {
      setIsConfigured(false)
      setCurrentField(null)
    } else {
      setCurrentField(null)
    }

    dispatch(clearFields(Forms.PLAN_FILTER_FORM, false, false, currentField.id))
  }

  const handleSetField = (field: PlanFilterField & { idx?: number }) => {
    setCurrentField(field)
  }

  const applyFilters = () => {
    const fieldFilters = filter.map((item) => generateFieldFilter(item, fields))
    setFieldFilters(fieldFilters)
    closeDrawer()
  }

  const onClickClear = () => {
    clearFilters()
    setFieldFilters([])
  }

  React.useEffect(() => {
    if (isOpen && appliedFieldFilters?.length) {
      const formFilter = appliedFieldFilters.map((f) => generateFieldValueFromFilter(f, fields))

      dispatch(initialize(Forms.PLAN_FILTER_FORM, { filter: formFilter }))
    }
  }, [isOpen])

  const showAddedFilters = !!filter.length && !isConfigured
  const showFiltersList = isConfigured && !currentField
  const showFieldSettings = currentField
  const showPlaceholder = !filter.length && !isConfigured

  return (
    <DrawerModal width='400px' isOpen={isOpen} hideModal={hideModal} PaperProps={PAPER_PROPS}>
      <Header>
        <span>Filter</span>
        <CloseIcon onClick={closeDrawer} />
      </Header>
      <Container>
        {showPlaceholder && (
          <Wrapper>
            <div>This view has no advanced filters.</div>
            <IconButton onClick={() => setIsConfigured(true)} size='large'>
              <PlusIcon htmlColor={colors.purple500} />
            </IconButton>
          </Wrapper>
        )}

        {showAddedFilters && (
          <Filters
            data={filter}
            fields={fields}
            setField={handleSetField}
            removeFilter={removeFilter}
            setIsConfigured={setIsConfigured}
          />
        )}

        {showFiltersList && (
          <Fields filter={filter} fields={fields} setField={handleSetField} setIsConfigured={setIsConfigured} />
        )}

        {showFieldSettings && <Field field={currentField} addField={addFilter} clearField={clearField} />}

        <ButtonsWrapper>
          <Button
            type='button'
            onClick={onClickClear}
            disabled={!filter.length}
            layout={BUTTON_LAYOUTS.PURPLE_INVERTED}>
            Clear
          </Button>
          <Button type='button' onClick={applyFilters} disabled={!filter.length} layout={BUTTON_LAYOUTS.PURPLE}>
            Apply
          </Button>
        </ButtonsWrapper>
      </Container>
    </DrawerModal>
  )
}

export default reduxForm<any, any>({
  form: Forms.PLAN_FILTER_FORM,
  destroyOnUnmount: false,
  enableReinitialize: true
})(PlanFilterDrawer)
